import React, { useState, useEffect } from 'react';
import { register } from '../api/auth';

function RegisterModal({ show, onClose, onRegistrationSuccess, onSwitchToLogin }) {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const validateForm = () => {
        if (!formData.email || !formData.password || !formData.confirmPassword) {
            setError('All fields are required');
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setError('');
        setResult(null);
        setLoading(true);

        try {
            const regResult = await register(formData.email, formData.password);

            if (regResult.success) {
                setResult({
                    success: true,
                    message: 'Registration successful! You can now log in.',
                    data: regResult.data
                });

                setFormData({
                    email: '',
                    password: '',
                    confirmPassword: ''
                });

                if (onRegistrationSuccess) {
                    onRegistrationSuccess(regResult.data);
                }
            } else {
                setError(regResult.message);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Registration error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            setError('');
            setResult(null);
        }
    }, [show]);

    if (!show) return null;

    return (
        <>
            {/* Modal backdrop */}
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            ></div>

            {/* Modal dialog */}
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1050 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    role="document"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        {/* Modal header */}
                        <div className="modal-header">
                            <h5 className="modal-title">Create Account</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>

                        {/* Modal body */}
                        <div className="modal-body">
                            {/* Status alerts */}
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            {result && result.success && (
                                <div className="alert alert-success" role="alert">
                                    {result.message}
                                </div>
                            )}

                            {/* Registration form */}
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={loading || result?.success}
                                        required
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="password" className="form-label">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        disabled={loading || result?.success}
                                        required
                                        autoComplete="new-password"
                                        minLength="6"
                                    />
                                    <div className="form-text">Password must be at least 6 characters long.</div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="confirmPassword" className="form-label">
                                        Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        disabled={loading || result?.success}
                                        required
                                        autoComplete="new-password"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 mb-3"
                                    disabled={loading || result?.success}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Creating Account...
                                        </>
                                    ) : (
                                        'Create Account'
                                    )}
                                </button>

                                {result?.success ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary w-100"
                                        onClick={onSwitchToLogin}
                                    >
                                        Proceed to Login
                                    </button>
                                ) : (
                                    <div className="text-center mt-3">
                                        <p className="mb-0">
                                            Already have an account?{' '}
                                            <button
                                                type="button"
                                                className="btn btn-link p-0"
                                                onClick={onSwitchToLogin}
                                            >
                                                Login
                                            </button>
                                        </p>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default RegisterModal;